// The card behind a board row, wired to the store.
//
// A board row could carry a label and nothing else. Two things had nowhere to live
// because of that, and they are the two the product is actually sold on:
//
//   the note   where a row CAME FROM — the recording, the timestamp, the frame.
//              `video → transcript → findings → item` is the chain positioning.md
//              §2 calls the differentiator, and a row with no prose cannot hold a
//              single link of it.
//   the thread the client arguing with a specific row in place, rather than by
//              email about "the third one down".
//
// Both are per-row and both are occasional, so neither earns a column. The popover
// itself is `RowCardView`, shared with the client's seat — the thread is a
// conversation between the two sides, so it would be odd for each side to draw it
// differently. This file is the store half: what the card reads, and what posting
// into it appends.

import { newId } from '../lib/ids'
import { showsToClient } from '../lib/model'
import { commentsFor, type ItemRecord } from '../lib/reducer'
import { useStore } from '../lib/store'
import { RichField } from '../routes/EntityDetail'
import { Composer } from './EntryThread'
import { RowCardPanel } from './RowCardView'

/** The open panel for one row, wired to the store. The mark that opens it is
 *  `RowCardMark`, in the row's last cell; the row holds the open state. */
export function RowCard({ item }: { item: ItemRecord }) {
  const { state, dispatch, version } = useStore()
  void version

  const comments = commentsFor(state, item.id)
  const shown = showsToClient(item.visibility)
  // Defensive: a projection restored from an older cache has no `note`, and one
  // undefined field should never be able to blank the whole app.
  const note = item.note ?? ''

  return (
    <RowCardPanel
      comments={comments.map((c) => ({
        id: c.id,
        body: c.body,
        at: c.createdAt,
        by: c.createdBy?.name ?? '',
        // On the team's side every comment is somebody else's by definition —
        // the highlight means "the client said this", and that is the client's own
        // reading of their own thread.
        mine: false,
      }))}
      noteSlot={
        <>
          <p className="eyebrow mb-1">Note</p>
          {/* The note travels with the row now (worker/client-view.ts), so the eye
              on this row decides whether this box is a scratchpad or something the
              client reads. That is worth saying where the typing happens rather
              than in a convention nobody remembers at the keyboard. */}
          {shown && (
            <p className="meta mb-1.5" style={{ color: 'var(--brass)' }}>
              The client reads this row, and this note with it.
            </p>
          )}
          <RichField
            value={note}
            placeholder="Notes, links, where this came from…"
            onSave={(v) =>
              void dispatch({ type: 'item.update', subject: item.id, data: { note: v } })
            }
          />
        </>
      }
      composer={
        <Composer
          placeholder="Add a comment…"
          submitLabel="Post"
          submitOnEnter
          minHeight={44}
          onSave={(body) =>
            void dispatch({
              type: 'comment.create',
              subject: newId('cmt'),
              data: { target: item.id, body },
            })
          }
        />
      }
    />
  )
}
